import { ResponsiveModal } from '@bridebook/aura';
import { useBreakpoint } from '@bridebook/ui/src/utils/hooks/use-breakpoint';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { clickedAnalytics } from '@bridebook/web/lib/ui/analytics';
import { useDispatch } from '@bridebook/web/lib/utils';
import { ShareModalProvider } from '../../../lib/share/share-modal-context';
import ShareModalLazy from '../../../lib/share/share-modal-lazy';
import { EditorSyncProvider } from '../main/desktop/editor-sync-context';
import { Rsvp } from './rsvp';
import { QuestionForm } from '@bridebook/web/components/wedding-website/rsvp/question-form/question-form';
import GuestRsvpPreview from '@bridebook/web/components/wedding-website/guest-rsvp/guest-rsvp-preview';

type TRsvpModalContext = {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  openPreviewModal: () => void;
  closePreviewModal: () => void;
  openModalEdit: (rsvpId: string) => void;
  rsvpQuestionId: string | undefined;
  setRsvpQuestionId: (rsvpQuestionId: string | undefined) => void;
};

const RsvpModalContext = createContext<TRsvpModalContext | undefined>(undefined);

export const useRsvpModal = (): TRsvpModalContext => {
  const context = useContext(RsvpModalContext);
  if (!context) {
    throw new Error('useRsvpModal must be used within a RsvpModalProvider');
  }
  return context;
};

const EditorSyncShareWrapper = ({ children }: { children: React.ReactNode }) => (
  <EditorSyncProvider noop>
    <ShareModalProvider>
      <ShareModalLazy />
      {children}
    </ShareModalProvider>
  </EditorSyncProvider>
);

export const RsvpModalProvider = ({ children }: { children: React.ReactNode }) => {
  const isDesktop = useBreakpoint('desktopSm');
  const dispatch = useDispatch();
  const [rsvpQuestionId, setRsvpQuestionId] = useState<string | undefined>(undefined);
  const [isModalOpen, setisModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  // Tracks whether the RSVP modal was open before opening preview,
  // so closing the preview can restore it. Ref because it's only
  // cross-callback communication — never drives rendering.
  const shouldRestoreRsvpModalRef = useRef(false);

  const openModal = useCallback(() => {
    setisModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setRsvpQuestionId(undefined);
    setisModalOpen(false);
  }, []);

  const openPreviewModal = useCallback(() => {
    shouldRestoreRsvpModalRef.current = isModalOpen;
    setisModalOpen(false);
    setRsvpQuestionId(undefined);
    setIsPreviewModalOpen(true);
    dispatch(
      clickedAnalytics({
        category: 'guestManagement',
        clickedLocation: 'rsvpForm',
        clickedSection: 'previewButton',
        cta: 'Preview',
      }),
    );
  }, [dispatch, isModalOpen]);

  const closePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    if (shouldRestoreRsvpModalRef.current) {
      setisModalOpen(true);
      shouldRestoreRsvpModalRef.current = false;
    }
  }, []);

  const openModalEdit = useCallback(
    (rsvpId: string) => {
      setRsvpQuestionId(rsvpId);
      openModal();
    },
    [openModal, setRsvpQuestionId],
  );

  const value = useMemo(
    () => ({
      isModalOpen,
      openModal,
      closeModal,
      openPreviewModal,
      closePreviewModal,
      openModalEdit,
      rsvpQuestionId,
      setRsvpQuestionId,
    }),
    [
      isModalOpen,
      openModal,
      closeModal,
      openPreviewModal,
      closePreviewModal,
      openModalEdit,
      rsvpQuestionId,
    ],
  );

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeModal();
    },
    [closeModal],
  );

  const handlePreviewOnOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closePreviewModal();
    },
    [closePreviewModal],
  );

  if (!isModalOpen && !isPreviewModalOpen) {
    return <RsvpModalContext.Provider value={value}>{children}</RsvpModalContext.Provider>;
  }

  return (
    <RsvpModalContext.Provider value={value}>
      {children}
      <EditorSyncShareWrapper>
        {isModalOpen && (
          <ResponsiveModal isOpen={isModalOpen} onOpenChange={handleOnOpenChange}>
            {rsvpQuestionId ? <QuestionForm /> : <Rsvp />}
          </ResponsiveModal>
        )}
        {isPreviewModalOpen &&
          (isDesktop ? (
            <GuestRsvpPreview onClose={closePreviewModal} variant="desktop" />
          ) : (
            <ResponsiveModal
              isOpen={isPreviewModalOpen}
              onOpenChange={handlePreviewOnOpenChange}
              height="fixed-height">
              <GuestRsvpPreview onClose={closePreviewModal} variant="mobile" />
            </ResponsiveModal>
          ))}
      </EditorSyncShareWrapper>
    </RsvpModalContext.Provider>
  );
};
